import { useCallback, useMemo, useState } from 'react';

import type { TablePaginationConfig } from 'antd';

import { useConditionalTableScroll } from '@/shared/hooks/useConditionalTableScroll';
import { DEFAULT_PAGE_SIZE, PAGINATION_OPTIONS } from '@/shared/lib/constants';

type UsePaginationOptions = {
  dataLength: number;
  viewportOffset: number;
  rowHeight?: number;
  headerHeight?: number;
  paginationHeight?: number;
  initialPage?: number;
  initialPageSize?: number;
  onPageChange?: (page: number, pageSize: number) => void;
};

type UsePaginationReturn = {
  currentPage: number;
  currentPageSize: number;
  paginationConfig: TablePaginationConfig;
  scrollConfig: { y: string } | undefined;
};

export const usePagination = ({
  dataLength,
  viewportOffset,
  rowHeight,
  headerHeight,
  paginationHeight,
  initialPage = 1,
  initialPageSize = DEFAULT_PAGE_SIZE,
  onPageChange,
}: UsePaginationOptions): UsePaginationReturn => {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [currentPageSize, setCurrentPageSize] = useState(initialPageSize);

  const handlePaginationChange = useCallback(
    (page: number, pageSize: number) => {
      setCurrentPage(page);
      setCurrentPageSize(pageSize);
      onPageChange?.(page, pageSize);
    },
    [onPageChange]
  );

  const scrollConfig = useConditionalTableScroll({
    dataLength,
    pageSize: currentPageSize,
    viewportOffset,
    rowHeight,
    headerHeight,
    paginationHeight,
  });

  const paginationConfig: TablePaginationConfig = useMemo(
    () => ({
      current: currentPage,
      pageSize: currentPageSize,
      pageSizeOptions: [...PAGINATION_OPTIONS],
      showSizeChanger: true,
      locale: { items_per_page: '' },
      showTotal: (total, range) => `(${total}개 중 ${range[0]}-${range[1]})`,
      onChange: handlePaginationChange,
    }),
    [currentPage, currentPageSize, handlePaginationChange]
  );

  return {
    currentPage,
    currentPageSize,
    paginationConfig,
    scrollConfig,
  };
};
